import useTranslation from 'hooks/useTranslation/useTranslation';
import { useCallback, useMemo } from 'react';
import { createDeviceGroupFilterChips } from 'v2/filters/deviceGroups/createDeviceGroupFilterChips';
import { createDeviceGroupFilterSections } from 'v2/filters/deviceGroups/createDeviceGroupFilterSections';
import { filterStateDefault } from 'v2/filters/deviceGroups/deviceGroupFilterConstants';
import { DeviceGroupsFilterState, FilterState } from 'v2/filters/deviceGroups/deviceGroupFilterTypes';
import { filterDeviceGroups } from 'v2/filters/deviceGroups/filterDeviceGroups';
import { parseParamToBooleanRecord } from 'v2/filters/filterUrlParams';
import { hasEnabledFilter } from 'v2/filters/hasEnabledFilter';
import { DeviceGroupRow } from 'v2/hooks/useDeviceGroups/useDeviceGroups';
import { useFilterSearchParams } from 'v2/hooks/useFilterSearchParams/useFilterSearchParams';
import { useDeviceGroupSearch } from 'v2/hooks/useSearch/useDeviceGroupSearch';

interface Params {
  deviceGroups: DeviceGroupRow[];
}

type DeviceGroupFilterParam = 'search' | 'state';

const DEVICE_GROUP_FILTER_PARAM_KEYS: DeviceGroupFilterParam[] = ['search', 'state'];

export const useDeviceGroupsSearchAndFilters = ({ deviceGroups }: Params) => {
  const { translation } = useTranslation();

  const { searchParams, updateFilterParams, updateMultiSelectFilter } =
    useFilterSearchParams<DeviceGroupFilterParam>(DEVICE_GROUP_FILTER_PARAM_KEYS);

  const searchParam = searchParams.get('search');
  const stateParam = searchParams.get('state');

  const filterStr = searchParam ?? '';
  const filterState = useMemo(() => parseParamToBooleanRecord(stateParam, filterStateDefault), [stateParam]);

  const setFilterStr = useCallback(
    (value: string) => updateFilterParams(() => ({ search: value === '' ? undefined : value })),
    [updateFilterParams]
  );

  const setFilterState = useCallback(
    (value: FilterState | ((current: FilterState) => FilterState)) =>
      updateMultiSelectFilter('state', filterStateDefault, value),
    [updateMultiSelectFilter]
  );

  const clearAllFilters = useCallback(() => {
    updateFilterParams(() => ({ search: undefined, state: undefined }));
  }, [updateFilterParams]);

  const isAnyFilterEnabled = useMemo(() => hasEnabledFilter(filterStr, filterState), [filterStr, filterState]);

  const filters: DeviceGroupsFilterState = useMemo(
    () => ({ filterState, setFilterState, clearAllFilters, isAnyFilterEnabled }),
    [filterState, setFilterState, clearAllFilters, isAnyFilterEnabled]
  );

  const searchedDeviceGroups = useDeviceGroupSearch({ deviceGroups, search: filterStr, translation });

  const filteredDeviceGroups = useMemo(
    () => filterDeviceGroups({ deviceGroups: searchedDeviceGroups, filterState }),
    [searchedDeviceGroups, filterState]
  );

  const countCaption = `${filteredDeviceGroups.length} ${translation.of} ${deviceGroups.length} ${translation.device_groups}`;

  const sections = useMemo(() => createDeviceGroupFilterSections({ translation, filters }), [translation, filters]);
  const chips = useMemo(() => createDeviceGroupFilterChips({ translation, filters }), [translation, filters]);

  return {
    filterStr,
    setFilterStr,
    filteredDeviceGroups,
    sections,
    chips,
    countCaption,
    clearAllFilters
  };
};
